import jsQR from "jsqr";
import { TotpAlgorithm, UpdateCredentialTotpRequest } from "@app-types/tap-partner-vault";

const VALID_ALGORITHMS: TotpAlgorithm[] = ["SHA1", "SHA256", "SHA512"];

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to load QR image"));
    image.src = src;
  });

const normalizeSecret = (secret: string): string =>
  secret.toUpperCase().replace(/[\s-]/g, "");

export async function decodeQrImageFile(file: File): Promise<string> {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await loadImage(objectUrl);
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas is not available for QR decoding");
    }

    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const result = jsQR(imageData.data, imageData.width, imageData.height);

    if (!result?.data) {
      throw new Error("No QR code could be detected in the selected image");
    }

    return result.data;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function parseOtpauthUri(uri: string): UpdateCredentialTotpRequest {
  let parsed: URL;

  try {
    parsed = new URL(uri);
  } catch {
    throw new Error("The QR code does not contain a valid URL");
  }

  if (parsed.protocol !== "otpauth:" || parsed.hostname.toLowerCase() !== "totp") {
    throw new Error("The QR code must contain an otpauth://totp URI");
  }

  const rawLabel = decodeURIComponent(parsed.pathname.replace(/^\/+/, "")).trim();
  if (!rawLabel) {
    throw new Error("The TOTP URI is missing an account label");
  }

  const separatorIndex = rawLabel.indexOf(":");
  const issuerFromLabel =
    separatorIndex >= 0 ? rawLabel.slice(0, separatorIndex).trim() : "";
  const accountLabel =
    separatorIndex >= 0 ? rawLabel.slice(separatorIndex + 1).trim() : rawLabel;

  const issuer = (parsed.searchParams.get("issuer") || issuerFromLabel).trim();
  const secret = normalizeSecret(parsed.searchParams.get("secret") || "");
  const algorithm = (
    parsed.searchParams.get("algorithm")?.toUpperCase() || "SHA1"
  ) as TotpAlgorithm;
  const digits = Number(parsed.searchParams.get("digits") || "6");
  const periodSeconds = Number(parsed.searchParams.get("period") || "30");

  if (!issuer) {
    throw new Error("The TOTP URI is missing an issuer");
  }

  if (!accountLabel) {
    throw new Error("The TOTP URI is missing an account label");
  }

  if (!secret || /[^A-Z2-7]/.test(secret)) {
    throw new Error("The TOTP URI contains an invalid Base32 secret");
  }

  if (!VALID_ALGORITHMS.includes(algorithm)) {
    throw new Error("The TOTP URI contains an unsupported algorithm");
  }

  if (!Number.isInteger(digits) || digits < 6 || digits > 8) {
    throw new Error("The TOTP URI contains an unsupported digit length");
  }

  if (!Number.isInteger(periodSeconds) || periodSeconds < 15 || periodSeconds > 120) {
    throw new Error("The TOTP URI contains an unsupported refresh period");
  }

  return {
    issuer,
    account_label: accountLabel,
    secret,
    algorithm,
    digits,
    period_seconds: periodSeconds,
  };
}
